// 📁 src/components/MapCompteurs.jsx
import { GoogleMap, Marker, InfoWindow, Polygon } from '@react-google-maps/api';
import { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const containerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
};

const defaultCenter = {
  lat: 34.0209, // Rabat, Morocco
  lng: -6.8416
};

const getColorByIndex = (index) => {
  const colors = [
    '#FF5722', '#3F51B5', '#009688', '#FFC107', '#E91E63',
    '#4CAF50', '#9C27B0', '#00BCD4', '#CDDC39', '#795548'
  ];
  return colors[index % colors.length];
};

const getPolygonCenter = (coords) => {
  const lats = coords.map(p => p.lat);
  const lngs = coords.map(p => p.lng);
  return {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
  };
};

export default function MapCompteurs() {
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [zones, setZones] = useState([]);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };

        console.log('🔄 Loading contracts with location data...');

        // Try to get contracts with location data (similar to mobile app)
        let contractsData;
        try {
          // First try to get contracts with location
          const contractsRes = await axios.get(API + '/api/contracts', { headers });
          contractsData = contractsRes.data;
          console.log('📊 Raw contracts data:', contractsData);
          
          // Check if contracts have location data
          const contractsWithLocation = contractsData.filter(c => 
            (c.latitude && c.longitude) || (c.posx && c.posy)
          );
          
          if (contractsWithLocation.length === 0) {
            console.log('⚠️ No contracts with location found, adding test coordinates...');
            // Add test coordinates if no location data exists (similar to mobile fallback)
            contractsData = contractsData.map((contract, index) => ({
              ...contract,
              latitude: (34.0209 + (index * 0.01)).toString(), // Rabat area with spread
              longitude: (-6.8416 - (index * 0.01)).toString(),
              // Also add fallback for old format
              posy: (34.0209 + (index * 0.01)).toString(),
              posx: (-6.8416 - (index * 0.01)).toString()
            }));
          }
          
        } catch (error) {
          console.error('❌ Error fetching contracts:', error);
          contractsData = [];
        }

        // Filter contracts with valid coordinates
        const validContracts = contractsData.filter(c => {
          const lat = parseFloat(c.latitude || c.posy);
          const lng = parseFloat(c.longitude || c.posx);
          return !isNaN(lat) && !isNaN(lng);
        });

        console.log(`📍 Valid contracts with coordinates: ${validContracts.length}/${contractsData.length}`);

        // Load zones/sectors
        let mappedZones = [];
        try {
          const zonesRes = await axios.get(API + '/api/secteurs', { headers });
          mappedZones = zonesRes.data.filter(z => z.coords?.length > 0).map((z, i) => {
            const coords = z.coords.map(c => ({
              lat: parseFloat(c.lat),
              lng: parseFloat(c.lng)
            }));
            return {
              ...z,
              color: getColorByIndex(i),
              coords,
              center: getPolygonCenter(coords)
            };
          });
        } catch (error) {
          console.error('⚠️ Error loading zones:', error);
        }

        setContracts(validContracts);
        setZones(mappedZones);
        
        if (validContracts.length > 0) {
          console.log('🗺️ Sample contract coordinates:', 
            validContracts.slice(0, 3).map(c => ({
              name: `${c.nomclient} ${c.prenomclient}`,
              lat: parseFloat(c.latitude || c.posy),
              lng: parseFloat(c.longitude || c.posx)
            }))
          );
        }

      } catch (err) {
        console.error('❌ Error loading map data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, zoneFilter]);

  const filteredContracts = contracts.filter(c => {
    const nameMatch = `${c.nomclient || ''} ${c.prenomclient || ''}`.toLowerCase().includes(search.toLowerCase());
    const codeMatch = (c.codeqr || '').toLowerCase().includes(search.toLowerCase());
    return nameMatch || codeMatch;
  });

  const displayedZones = zoneFilter === 'all'
    ? zones
    : zones.filter(z => z.ids && z.ids.toString() === zoneFilter);

  // Calculate map center based on available data
  const mapCenter = (() => {
    if (filteredContracts.length > 0) {
      const firstContract = filteredContracts[0];
      const lat = parseFloat(firstContract.latitude || firstContract.posy);
      const lng = parseFloat(firstContract.longitude || firstContract.posx);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    if (displayedZones.length > 0) {
      return displayedZones[0].center;
    }
    return defaultCenter;
  })();

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>📍 Localisation des Compteurs</h2>
        <div>Chargement de la carte...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>📍 Localisation des Compteurs</h2>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px', justifyContent: 'space-between'
      }}>
        <input
          type="text"
          placeholder="🔎 Rechercher contrat, nom ou prénom..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flexGrow: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '250px' }}
        />
        <select
          value={zoneFilter}
          onChange={e => setZoneFilter(e.target.value)}
          style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ccc', minWidth: '200px' }}
        >
          <option value="all">🌍 Toutes les zones</option>
          {zones.map((z, i) => (
            <option key={i} value={z.ids}>{z.nom}</option>
          ))}
        </select>
      </div>

      <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={13}>
        {displayedZones.map((z, i) => (
          <Fragment key={`zone-${i}`}>
            <Polygon
              paths={z.coords}
              options={{
                fillColor: z.color,
                fillOpacity: 0.2,
                strokeColor: z.color,
                strokeOpacity: 0.8,
                strokeWeight: 2
              }}
            />
            <InfoWindow
              position={z.center}
              options={{ pixelOffset: new window.google.maps.Size(0, -20), disableAutoPan: true }}
            >
              <div style={{ fontWeight: 'bold', color: '#333' }}>{z.nom}</div>
            </InfoWindow>
          </Fragment>
        ))}

        {filteredContracts.map((contract, index) => {
          const lat = parseFloat(contract.latitude || contract.posy);
          const lng = parseFloat(contract.longitude || contract.posx);
          
          // Skip invalid coordinates
          if (isNaN(lat) || isNaN(lng)) {
            return null;
          }
          
          return (
            <Marker
              key={`contract-${contract.idcontract || contract.idclient}-${index}`}
              position={{ lat, lng }}
              onClick={() => setSelected(contract)}
              title={`${contract.nomclient || ''} ${contract.prenomclient || ''}`}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="12" fill="#1976D2" stroke="white" stroke-width="3"/>
                    <circle cx="16" cy="16" r="6" fill="white"/>
                    <text x="16" y="20" text-anchor="middle" fill="#1976D2" font-size="10" font-weight="bold">💧</text>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(32, 32),
                anchor: new window.google.maps.Point(16, 16)
              }}
            />
          );
        })}

        {selected && (
          <InfoWindow
            position={{ 
              lat: parseFloat(selected.latitude || selected.posy), 
              lng: parseFloat(selected.longitude || selected.posx) 
            }}
            onCloseClick={() => setSelected(null)}
          >
            <div style={{ minWidth: '250px', padding: '10px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1976D2' }}>
                {selected.nomclient} {selected.prenomclient}
              </h4>
              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                <p style={{ margin: '5px 0' }}>
                  <strong>📄 Contrat:</strong> {selected.codeqr || selected.idcontract}
                </p>
                <p style={{ margin: '5px 0' }}>
                  <strong>📍 Adresse:</strong> {selected.adresse || 'Non spécifiée'}
                </p>
                <p style={{ margin: '5px 0' }}>
                  <strong>📞 Téléphone:</strong> {selected.tel || 'Non spécifié'}
                </p>
                {selected.marquecompteur && (
                  <p style={{ margin: '5px 0' }}>
                    <strong>⚡ Compteur:</strong> {selected.marquecompteur} - {selected.numeriescompteur}
                  </p>
                )}
                {selected.secteur_nom && (
                  <p style={{ margin: '5px 0' }}>
                    <strong>🏘️ Secteur:</strong> {selected.secteur_nom}
                  </p>
                )}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '14px', color: '#666' }}>
          📊 {filteredContracts.length} compteur(s) affiché(s)
          {search && ` • Recherche: "${search}"`}
          {zoneFilter !== 'all' && ` • Zone filtrée`}
        </div>
        
        {filteredContracts.length === 0 && (
          <div style={{ 
            width: '100%', 
            textAlign: 'center', 
            color: '#888', 
            fontStyle: 'italic',
            marginTop: '10px',
            padding: '20px',
            border: '2px dashed #ddd',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9'
          }}>
            {search || zoneFilter !== 'all' 
              ? '🔍 Aucun compteur trouvé avec les critères de recherche actuels'
              : '📍 Aucun compteur avec coordonnées GPS trouvé'
            }
          </div>
        )}
      </div>
    </div>
  );
}
